import React from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { usePrerenderReady, useSpaLinkInterceptor,
  ErrorBoundary,
  UpdateAvailableBanner,
  VERSION_ANNOUNCED_EVENT,
} from '@byscott-io/corebyscott';
import { usePageTracking, useMetricsIdentity } from '@byscott-io/metricsbyscott-web';
import {
  configureAuth,
  AuthProvider,
  ThemeProvider,
  NotificationProvider,
  LoginForm,
  RegisterForm,
  ForgotPasswordForm,
  ResetPasswordForm,
  AcceptInvitationPage,
  ContainersPage,
  ProtectedRoute as AuthProtectedRoute,
  PublicRoute,
  MarketingRoute,
  storage,
  useAuth,
  FormGuardProvider,
} from '@byscott-io/corebyscott';
import { configureCable, PresenceProvider, useWaldoEvent } from '@byscott-io/where-is-waldo';
import Layout from './components/layout/Layout';
import Dashboard from './pages/Dashboard';
import RecipesPage from './pages/RecipesPage';
import RecipePage from './pages/RecipePage';
import PublicRecipesPage from './pages/PublicRecipesPage';
import ShoppingPage from './pages/ShoppingPage';
import MealPlannerPage from './pages/MealPlannerPage';
import HouseholdMembersPage from './pages/HouseholdMembersPage';
import AdminPage from './pages/AdminPage';
import ContributeRecipePage from './pages/ContributeRecipePage';
import PendingRecipesPage from './pages/PendingRecipesPage';
import { PublicChat } from '@byscott-io/corebyscott';
import { publicRecipes } from './api/client';
import SupportPage from './pages/SupportPage';
import ProfilePage from './pages/ProfilePage';

// Marketing pages
import FeaturesPage from './pages/marketing/FeaturesPage';
import AboutPage from './pages/marketing/AboutPage';
import ContactPage from './pages/marketing/ContactPage';
import HelpPage from './pages/marketing/HelpPage';

// Configure corebyscott for recipesbyscott
configureAuth({
  appName: 'RecipesByScott',
  containerName: 'organization',
  containerLabel: 'Kitchen',
  containerLabelPlural: 'Kitchens',
  containerHeader: 'X-Organization-Id',
  dashboardPath: '/app',
  roles: ['owner', 'admin', 'member'],
  adminRoles: ['owner', 'admin'],
  features: {
    invitations: true,
    passwordReset: true,
    sessions: true,
    multiContainer: true,
    registration: true,
    mfa: true,
  },
  jwtIncludeContainer: true,
  // Anonymous chat + public submissions (#520/#521). Server-side
  // provider + site key come from Rails-rendered meta tags
  // (<meta name="bot-check-provider" content="turnstile" /> and
  // <meta name="turnstile-site-key" content="0xAAA..." />). Falls back
  // to :off so dev environments don't block on credentials.
  botCheckProvider: typeof document !== 'undefined'
    ? (document.querySelector('meta[name="bot-check-provider"]')?.content || 'off')
    : 'off',
  turnstileSiteKey: typeof document !== 'undefined'
    ? document.querySelector('meta[name="turnstile-site-key"]')?.content || null
    : null,
  footer: {
    enabled: true,
    brand: 'IdeasByScott.com',
    brandUrl: 'https://byscott.io',
    links: [],
  },
  marketing: {
    dashboardLabel: 'Go to My Kitchen',
    navItems: [
      { path: '/features', label: 'Features' },
      { path: '/about', label: 'About' },
      { path: '/help', label: 'Help' },
      { path: '/contact', label: 'Contact' },
    ],
    footer: {
      companyName: 'IdeasByScott',
      companyDescription: 'Building practical tools for real-world problems.',
      productLinks: [
        { label: 'Features', href: '/features' },
        { label: 'Browse Recipes', href: '/' },
        { label: 'Help', href: '/help' },
      ],
      companyLinks: [
        { label: 'About', href: '/about' },
        { label: 'Contact', href: '/contact' },
      ],
      legalLinks: [
        { label: 'Privacy Policy', href: 'https://byscott.io/privacy-policy' },
        { label: 'Terms of Service', href: 'https://byscott.io/terms' },
      ],
      copyright: `© ${new Date().getFullYear()} IdeasByScott. All rights reserved.`,
    },
  },
  logo: (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-7 h-7">
      <path d="M11 9H9V2H7v7H5V2H3v7c0 2.12 1.66 3.84 3.75 3.97V22h2.5v-9.03C11.34 12.84 13 11.12 13 9V2h-2v7zm5-3v8h2.5v8H21V2c-2.76 0-5 2.24-5 4z"/>
    </svg>
  ),
});

// Real-time cable connection (#626): JWT auth via corebyscott storage token.
configureCable({
  url: '/cable',
  getToken: () => storage.getToken(),
});

function PageTracker() {
  const location = useLocation();
  usePageTracking(location.pathname);
  return null;
}

// Sync authenticated identity (user_id / member_id / container_id) into the
// metrics client. Boot-seed (application.js) covers the pre-/me window.
function MetricsIdentity() {
  useMetricsIdentity(useAuth());
  return null;
}

function ProtectedRoute({ children }) {
  return (
    <AuthProtectedRoute>
      <Layout>{children}</Layout>
    </AuthProtectedRoute>
  );
}

function AdminRoute({ children }) {
  return (
    <AuthProtectedRoute requiredRoles={['owner', 'admin']}>
      <Layout>{children}</Layout>
    </AuthProtectedRoute>
  );
}

function SpaLinkInterceptor({ children }) {
  useSpaLinkInterceptor();
  return children;
}

// TWO BOUNDARIES, doing different jobs. This inner one is inside the router and
// resets on navigation, so a page that throws is recovered by going elsewhere.
// The outer one in App sits OUTSIDE the providers, so it also catches a crash
// in a provider, which this one cannot -- it mounts underneath them. Without
// either, an uncaught render error unmounts the tree and leaves a blank page.
function RouteBoundary({ children }) {
  const location = useLocation();
  return <ErrorBoundary resetKey={location.pathname}>{children}</ErrorBoundary>;
}

// A deploy announces itself over waldo when the new container boots. Core
// cannot subscribe for itself -- it must not import the waldo client, since not
// every app has one -- so the app turns the broadcast into the window event
// core listens for. The payload is not used: it only prompts a fresh ask of
// /api/version, which is what actually decides.
function VersionAnnouncements() {
  useWaldoEvent('app_version', () => {
    window.dispatchEvent(new Event(VERSION_ANNOUNCED_EVENT));
  });
  return null;
}

export default function App() {
  usePrerenderReady();
  return (
    <ErrorBoundary>
    <ThemeProvider>
      <NotificationProvider>
        <AuthProvider>
          <PresenceProvider>
          <FormGuardProvider>
          <BrowserRouter>
            {/* Offers a refresh when a deploy has replaced the code this tab is
                running. Never reloads on its own. */}
            <UpdateAvailableBanner />
            <VersionAnnouncements />
            <PageTracker />
            <MetricsIdentity />
            <SpaLinkInterceptor>
            <RouteBoundary>
            <Routes>
            {/* Marketing Pages (public, no redirect for logged-in users) */}
            <Route
              path="/"
              element={
                <MarketingRoute>
                  <PublicRecipesPage />
                </MarketingRoute>
              }
            />
            <Route
              path="/features"
              element={
                <MarketingRoute>
                  <FeaturesPage />
                </MarketingRoute>
              }
            />
            <Route
              path="/about"
              element={
                <MarketingRoute>
                  <AboutPage />
                </MarketingRoute>
              }
            />
            <Route
              path="/contact"
              element={
                <MarketingRoute>
                  <ContactPage />
                </MarketingRoute>
              }
            />

            {/* Public Recipes redirects to home */}
            <Route path="/public/recipes" element={<Navigate to="/" replace />} />
            <Route
              path="/recipes/:id"
              element={
                <MarketingRoute>
                  <PublicRecipesPage />
                </MarketingRoute>
              }
            />
            <Route
              path="/contribute"
              element={
                <MarketingRoute>
                  <ContributeRecipePage />
                </MarketingRoute>
              }
            />
            <Route
              path="/help/*"
              element={
                <MarketingRoute>
                  <HelpPage />
                </MarketingRoute>
              }
            />

            {/* Auth Pages (redirect logged-in users to /app) */}
            <Route
              path="/login"
              element={
                <PublicRoute>
                  <LoginForm />
                </PublicRoute>
              }
            />
            <Route
              path="/register"
              element={
                <PublicRoute>
                  <RegisterForm />
                </PublicRoute>
              }
            />
            <Route
              path="/forgot-password"
              element={
                <PublicRoute>
                  <ForgotPasswordForm />
                </PublicRoute>
              }
            />
            <Route
              path="/reset-password"
              element={
                <PublicRoute>
                  <ResetPasswordForm />
                </PublicRoute>
              }
            />
            <Route
              path="/invitations/accept/:token"
              element={<AcceptInvitationPage />}
            />

            {/* App Pages (protected, requires auth) */}
            <Route
              path="/app"
              element={
                <ProtectedRoute>
                  <Dashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/app/recipes"
              element={
                <ProtectedRoute>
                  <RecipesPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/app/recipes/:id"
              element={
                <ProtectedRoute>
                  <RecipePage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/app/shopping"
              element={
                <ProtectedRoute>
                  <ShoppingPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/app/meals"
              element={
                <ProtectedRoute>
                  <MealPlannerPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/app/household"
              element={
                <ProtectedRoute>
                  <HouseholdMembersPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/app/admin"
              element={<Navigate to="/app/admin/team" replace />}
            />
            <Route
              path="/app/admin/pending-recipes"
              element={
                <AdminRoute>
                  <PendingRecipesPage />
                </AdminRoute>
              }
            />
            <Route
              path="/app/admin/:tab"
              element={
                <AdminRoute>
                  <AdminPage />
                </AdminRoute>
              }
            />
            <Route
              path="/app/support"
              element={
                <ProtectedRoute>
                  <SupportPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/app/profile"
              element={
                <ProtectedRoute>
                  <ProfilePage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/app/kitchens"
              element={
                <ProtectedRoute>
                  <ContainersPage />
                </ProtectedRoute>
              }
            />

            {/* Fallback */}
            <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
            </RouteBoundary>
            </SpaLinkInterceptor>
            {/* Site-wide AI chat — single persistent instance, switches authed/anon internally */}
            <PublicChat
              authenticatedTask={{
                taskType: 'RecipesChatTask',
                title: 'Recipes AI',
                placeholder: 'Ask about recipes, meal plans, or shopping...',
                welcomeMessage:
                  'Hi! I can help you find recipes, plan meals, and build shopping lists.',
                voiceEnabled: true,
                attachmentsEnabled: true,
              }}
              anonymousTask={{
                taskType: 'RecipesAnonymousChatTask',
                title: 'Recipes AI',
                welcomeMessage:
                  'Hi! Ask me anything about the public recipes — quick weeknight ideas, dietary swaps, cooking tips.',
                placeholder: 'What do you feel like cooking?',
                challengeUrl: publicRecipes.botCheckChallengeUrl,
              }}
            />
          </BrowserRouter>
          </FormGuardProvider>
          </PresenceProvider>
        </AuthProvider>
      </NotificationProvider>
    </ThemeProvider>
    </ErrorBoundary>
  );
}
